import { runInAction } from "mobx";

import { defined } from "@formant/core/common/defined";
import { matchesTags } from "@formant/core/common/matchesTags";
import { tagSetsToTags } from "@formant/core/common/tagSetsToTags";
import { ICommandTemplate } from "@formant/core/model/ICommandTemplate";
import { ITags } from "@formant/core/model/ITags";
import { Uuid } from "@formant/core/model/Uuid";

import { ReorderableCommandTemplateClient } from "../client/ReorderableCommandTemplateClient";
import {
    reorderCommandDisplayOrders,
    sortCommandTemplatesByDisplayOrder,
} from "../utils/commandTemplateOrder";
import { IAuthStore } from "./IAuthStore";
import { ICommandTemplateStore } from "./ICommandTemplateStore";
import { StoreCache } from "./StoreCache";

export class CommandTemplateStore implements ICommandTemplateStore {
    private listCache = new StoreCache<undefined, ICommandTemplate[]>();
    private cache = new StoreCache<Uuid, ICommandTemplate | false>();

    constructor(
        private commandTemplateClient: ReorderableCommandTemplateClient,
        private authStore: IAuthStore,
    ) {}

    list(): ICommandTemplate[] | undefined {
        const all = this.listAll();
        return all
            ? sortCommandTemplatesByDisplayOrder(all.filter(_ => _.enabled))
            : undefined;
    }

    listAll(): ICommandTemplate[] | undefined {
        const items = this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
        return items ? sortCommandTemplatesByDisplayOrder(items) : items;
    }

    listMatching(tags: ITags): ICommandTemplate[] {
        const all = this.list();
        if (!all) {
            return [];
        }
        return all.filter(_ =>
            matchesTags(
                tags,
                // for backwards compatability, if device filter is not set
                // use command template tags
                _.deviceFilter?.tags
                    ? tagSetsToTags(_.deviceFilter.tags)
                    : _.tags,
                {
                    logic: "or",
                    ignoreReservedTags: true,
                },
            ),
        );
    }

    async listMatchingAsync(tags: ITags): Promise<ICommandTemplate[]> {
        const all = await this.fetchList();
        const filtered = all.filter(_ =>
            matchesTags(
                tags,
                // for backwards compatability, if device filter is not set
                // use command template tags
                _.deviceFilter?.tags
                    ? tagSetsToTags(_.deviceFilter.tags)
                    : _.tags,
                {
                    logic: "or",
                    ignoreReservedTags: true,
                },
            ),
        );
        console.log("filtered", filtered, tags);
        return filtered;
    }

    get(id: Uuid): ICommandTemplate | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    async create(commandTemplate: ICommandTemplate): Promise<ICommandTemplate> {
        const created = await this.commandTemplateClient.create(
            await this.authStore.token(),
            commandTemplate,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    async update(id: Uuid, commandTemplate: Partial<ICommandTemplate>) {
        const updated = await this.commandTemplateClient.update(
            await this.authStore.token(),
            id,
            commandTemplate,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    save(commandTemplate: ICommandTemplate): Promise<ICommandTemplate> {
        const { id } = commandTemplate;
        if (id) {
            return this.update(id, commandTemplate);
        } else {
            return this.create(commandTemplate);
        }
    }

    async disable(id: Uuid) {
        return await this.update(id, { enabled: false });
    }

    async reorder(
        moveId: Uuid,
        placement: "before" | "after",
        dropId: Uuid,
    ): Promise<void> {
        const previous = await this.fetchList();
        const items = reorderCommandDisplayOrders(
            previous,
            moveId,
            placement,
            dropId,
        );
        if (items.length === 0) {
            return;
        }

        const rankById = new Map(items.map(_ => [_.id, _.displayOrder]));
        const projection = sortCommandTemplatesByDisplayOrder(
            previous.map(it => {
                const displayOrder = it.id ? rankById.get(it.id) : undefined;
                return displayOrder === undefined
                    ? it
                    : { ...it, displayOrder };
            }),
        );
        runInAction(() => {
            this.listCache.set(undefined, projection);
            projection.forEach(it => this.cache.set(defined(it.id), it));
        });

        try {
            await this.commandTemplateClient.reorder(
                await this.authStore.token(),
                items,
            );
        } catch (err) {
            runInAction(() => {
                this.listCache.set(undefined, previous);
                previous.forEach(it => this.cache.set(defined(it.id), it));
            });
            throw err;
        }
    }

    async fetchGet(id: Uuid): Promise<ICommandTemplate | false> {
        const commandTemplate = await this.commandTemplateClient.get(
            await this.authStore.token(),
            id,
        );
        const value = commandTemplate || false;
        this.cache.set(id, value);
        return value;
    }

    async fetchList(): Promise<ICommandTemplate[]> {
        const value = sortCommandTemplatesByDisplayOrder(
            await this.commandTemplateClient.list(await this.authStore.token()),
        );
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }
}
